/**
 * Target-country picker shown as the final onboarding step (Talk360-style).
 *
 * Why: Asking "Which country do you want to call?" up-front lets the home
 * screen show personalised rates, promo cards and country-specific copy
 * BEFORE the user has dialled a number. Persisted to localStorage so the
 * dashboard can hydrate instantly on first paint.
 *
 * The choice is non-binding - users can call any country at any time.
 */
import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Search, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getCountryCallingCode } from "libphonenumber-js";
import phoneIllustration from "@/assets/elefa-app-icon.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { NativeFlag } from "@/components/connect/NativeFlag";
import { ALL_ISO2_CODES, iso2ToFlagEmoji } from "@/lib/country-flags";
import { cn } from "@/lib/utils";

export const TARGET_COUNTRY_STORAGE_KEY = "elefa.target_country.v1";

interface TargetCountry {
  iso2: string;
  name: string;
  flag: string;
  dialCode: string;
}

// Top diaspora-call destinations for elefa's customer base. Ordering mirrors
// real volume from voice_routes / call_history (verified 2026-04 baseline).
const POPULAR_TARGETS: TargetCountry[] = [
  { iso2: "NG", name: "Nigeria",      flag: "🇳🇬", dialCode: "234" },
  { iso2: "ZA", name: "South Africa", flag: "🇿🇦", dialCode: "27"  },
  { iso2: "ET", name: "Ethiopia",     flag: "🇪🇹", dialCode: "251" },
  { iso2: "KE", name: "Kenya",        flag: "🇰🇪", dialCode: "254" },
  { iso2: "ZW", name: "Zimbabwe",     flag: "🇿🇼", dialCode: "263" },
  { iso2: "IN", name: "India",        flag: "🇮🇳", dialCode: "91"  },
  { iso2: "SS", name: "South Sudan",  flag: "🇸🇸", dialCode: "211" },
  { iso2: "BD", name: "Bangladesh",   flag: "🇧🇩", dialCode: "880" },
];

export function persistTargetCountry(c: TargetCountry | null) {
  try {
    if (c) localStorage.setItem(TARGET_COUNTRY_STORAGE_KEY, JSON.stringify(c));
    else   localStorage.removeItem(TARGET_COUNTRY_STORAGE_KEY);
  } catch { /* noop */ }
}

export function getTargetCountry(): TargetCountry | null {
  try {
    const raw = localStorage.getItem(TARGET_COUNTRY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

interface Props {
  selected: TargetCountry | null;
  onSelect: (c: TargetCountry | null) => void;
  onContinue: () => void;
  onSkip: () => void;
}

/**
 * Build the full ISO-3166 list with localized country names via Intl.DisplayNames.
 * Memoised at module load so opening the sheet is instant.
 */
let _fullCountryCache: TargetCountry[] | null = null;
function getFullCountryList(locale: string): TargetCountry[] {
  if (_fullCountryCache) return _fullCountryCache;
  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([locale || "en"], { type: "region" });
  } catch { /* unsupported - fall back to ISO codes */ }

  const list: TargetCountry[] = [];
  for (const iso2 of ALL_ISO2_CODES) {
    let dialCode = "";
    try { dialCode = getCountryCallingCode(iso2); } catch { continue; }
    if (!dialCode) continue;
    const name = (displayNames?.of(iso2) ?? iso2) || iso2;
    list.push({ iso2, name, flag: iso2ToFlagEmoji(iso2), dialCode });
  }
  list.sort((a, b) => a.name.localeCompare(b.name));
  _fullCountryCache = list;
  return list;
}

export function TargetCountryStep({ selected, onSelect, onContinue, onSkip }: Props) {
  const { t, i18n } = useTranslation("portal");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");

  const fullList = useMemo(() => getFullCountryList(i18n.language), [i18n.language]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fullList;
    return fullList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        c.dialCode.includes(q.replace(/^\+/, "")),
    );
  }, [fullList, query]);

  const isPopular = (iso2: string) => POPULAR_TARGETS.some((p) => p.iso2 === iso2);

  return (
    <motion.div
      key="target-country"
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="w-full max-w-md mx-auto flex flex-col text-left"
    >
      <h1 className="text-2xl font-bold text-foreground leading-tight">
        {t("targetCountryTitle", "Where is home?")}
      </h1>
      <p className="text-sm text-muted-foreground mt-2 mb-6">
        {t("targetCountryBody", "We will show you the rate before your first call. No commitment to top up.")}
      </p>

      {/* Phone → flag transition (mirrors Talk360 pattern).
          Flag preview uses a 3:2 rectangular frame with object-contain so the
          full flag shows (object-cover was cropping edges, e.g. ZA chevron). */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <img src={phoneIllustration} alt="" className="w-14 h-14 object-contain rounded-2xl" />
        <span className="text-2xl text-primary/60 tracking-tighter" aria-hidden="true">›››</span>
        <div
          className={cn(
            "h-14 w-20 rounded-xl flex items-center justify-center overflow-hidden ring-2 ring-border bg-muted/40 transition-all",
            selected && "ring-primary bg-primary/5 scale-105",
          )}
        >
          {selected ? (
            <NativeFlag
              emoji={selected.flag}
              label={selected.name}
              className="h-10 w-16"
            />
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 mb-2">
        {POPULAR_TARGETS.map((c) => {
          const isOn = selected?.iso2 === c.iso2;
          return (
            <button
              key={c.iso2}
              type="button"
              onClick={() => onSelect(isOn ? null : c)}
              className={cn(
                "flex items-center gap-2 rounded-full pl-1.5 pr-3 py-1.5 ring-1 transition-all active:scale-[0.97] text-left",
                isOn
                  ? "ring-primary bg-primary/10 text-foreground"
                  : "ring-border bg-card hover:bg-muted/40 text-foreground",
              )}
              aria-pressed={isOn}
            >
              <NativeFlag emoji={c.flag} label={c.name} className="w-7 h-5 shrink-0 rounded-sm" />
              <span className="text-sm font-medium truncate">{c.name}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className={cn(
            "col-span-2 flex items-center justify-center gap-2 rounded-full px-4 py-2 ring-1 ring-dashed ring-border text-sm transition-all active:scale-[0.97]",
            selected && !isPopular(selected.iso2)
              ? "ring-primary bg-primary/10 text-foreground"
              : "text-muted-foreground hover:bg-muted/40",
          )}
        >
          {selected && !isPopular(selected.iso2) ? (
            <>
              <NativeFlag
                emoji={selected.flag}
                label={selected.name}
                className="w-6 h-4 rounded-sm shrink-0"
              />
              <span className="font-medium truncate">{selected.name}</span>
              <span className="text-muted-foreground">· {t("change", "Change")}</span>
            </>
          ) : (
            <>
              <Search className="h-4 w-4" aria-hidden="true" />
              {t("targetCountryOthers", "Browse all countries…")}
            </>
          )}
        </button>
      </div>

      <div className="mt-6 space-y-2">
        <Button
          size="lg"
          className="w-full h-14 rounded-2xl text-base font-semibold"
          onClick={onContinue}
        >
          {t("targetCountryCta", "Show me the rate")}
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="lg"
          className="w-full h-12 rounded-2xl text-primary"
          onClick={onSkip}
        >
          {t("targetCountrySkip", "Skip for now")}
        </Button>
      </div>

      <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
        <SheetContent
          side="bottom"
          className="h-[85dvh] p-0 flex flex-col rounded-t-2xl"
        >
          <SheetHeader className="px-5 pt-5 pb-3 text-left">
            <SheetTitle>{t("targetCountryPickerTitle", "Choose a country")}</SheetTitle>
          </SheetHeader>
          <div className="px-5 pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("searchCountry", "Search country or dial code") as string}
                className="pl-9 h-11 rounded-xl"
                autoFocus={false}
                inputMode="search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-6">
            {filtered.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-10">
                {t("noCountriesFound", "No countries match your search")}
              </div>
            ) : (
              <ul role="listbox" className="space-y-0.5">
                {filtered.map((c) => {
                  const isOn = selected?.iso2 === c.iso2;
                  return (
                    <li key={c.iso2}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isOn}
                        onClick={() => {
                          onSelect(c);
                          setPickerOpen(false);
                          setQuery("");
                        }}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors",
                          isOn ? "bg-primary/10" : "hover:bg-muted/60 active:bg-muted",
                        )}
                      >
                        <NativeFlag
                          emoji={c.flag}
                          label={c.name}
                          className="w-8 h-6 rounded-sm shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{c.name}</div>
                          <div className="text-xs text-muted-foreground">+{c.dialCode}</div>
                        </div>
                        {isOn && <Check className="h-4 w-4 text-primary shrink-0" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </motion.div>
  );
}

export type { TargetCountry };
